import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Legal.css';

const Disclaimer = () => {
  return (
    <>
      <SEO
        title="Disclaimer"
        description="Legal disclaimer for SilverFox Media. Important information about our website templates and services."
        url="/disclaimer"
      />

      {/* Hero Section */}
      <section className="legal-hero">
        <div className="legal-hero__bg">
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--1" />
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--2" />
        </div>
        <div className="container">
          <motion.div
            className="legal-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Disclaimer</h1>
            <p>Last updated: January 15, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content section">
        <div className="container">
          <motion.div
            className="legal-content__wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="legal-section">
              <h2>1. General Information</h2>
              <p>
                The information provided on silverfoxmedia.co and through our services is for general informational purposes only. While we strive to provide accurate and up-to-date information, SilverFox Media makes no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Website Templates Disclaimer</h2>
              <h3>Demo Content</h3>
              <p>
                All website templates sold by SilverFox Media include placeholder content, including but not limited to:
              </p>
              <ul>
                <li>Fictional business names, addresses, and contact information</li>
                <li>Placeholder text and descriptions</li>
                <li>Stock or sample images</li>
                <li>Fictional testimonials and reviews</li>
                <li>Example pricing and service offerings</li>
              </ul>
              <p>
                <strong>This demo content is for illustration purposes only and must be replaced with your own legitimate business information before publishing your website.</strong>
              </p>

              <h3>Industry-Specific Templates</h3>
              <p>
                Our templates are designed for specific industries (law firms, real estate, healthcare, etc.) but are provided as general website templates. They do not constitute professional advice in any field. Users are responsible for ensuring their website content complies with all applicable laws, regulations, and professional standards in their industry.
              </p>

              <h3>Law Firm Templates</h3>
              <p>
                Templates designed for law firms and attorneys are marketing templates only. They do not constitute legal advice and should not be construed as such. Attorney advertising rules vary by jurisdiction. Users are solely responsible for ensuring their website complies with their state bar's attorney advertising rules and regulations.
              </p>

              <h3>Healthcare Templates</h3>
              <p>
                Templates designed for healthcare providers (chiropractors, medical practices, etc.) are marketing templates only. They do not constitute medical advice. Users are responsible for ensuring their website complies with applicable healthcare advertising regulations and does not make unsubstantiated health claims.
              </p>

              <h3>Real Estate Templates</h3>
              <p>
                Templates designed for real estate professionals include placeholder property listings and information. Users are responsible for ensuring compliance with fair housing laws, real estate advertising regulations, and MLS rules in their jurisdiction.
              </p>
            </div>

            <div className="legal-section">
              <h2>3. No Professional Advice</h2>
              <p>
                Nothing on this website or in our templates should be construed as:
              </p>
              <ul>
                <li>Legal advice</li>
                <li>Medical or health advice</li>
                <li>Financial or investment advice</li>
                <li>Real estate advice</li>
                <li>Any other professional advice</li>
              </ul>
              <p>
                For professional advice in any field, please consult a qualified professional licensed in your jurisdiction.
              </p>
            </div>

            <div className="legal-section">
              <h2>4. Third-Party Content and Links</h2>
              <p>
                Our website and templates may contain links to third-party websites or services. These links are provided for convenience only. SilverFox Media does not endorse, control, or assume responsibility for the content, privacy policies, or practices of any third-party websites or services.
              </p>
              <p>
                Images used in our templates may be sourced from stock photo providers or created for demonstration purposes. Users are responsible for replacing all images with properly licensed images for their own use.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Results Disclaimer</h2>
              <p>
                Any statements about potential results from using our templates or services (such as improved search rankings, increased traffic, or business growth) are not guarantees. Results vary based on numerous factors including:
              </p>
              <ul>
                <li>Your industry and market conditions</li>
                <li>Content quality and relevance</li>
                <li>SEO best practices implementation</li>
                <li>Marketing efforts and budget</li>
                <li>Competition in your market</li>
              </ul>
              <p>
                We do not guarantee any specific results from using our templates or services.
              </p>
            </div>

            <div className="legal-section">
              <h2>6. Technical Disclaimer</h2>
              <p>
                While we strive to provide high-quality, functional website templates:
              </p>
              <ul>
                <li>We do not guarantee compatibility with all browsers, devices, or hosting environments.</li>
                <li>We do not guarantee that templates will be free of bugs or errors.</li>
                <li>We do not guarantee uninterrupted availability of hosted websites.</li>
                <li>Performance may vary based on hosting provider, content, and traffic levels.</li>
              </ul>
              <p>
                Templates are provided "as is" and users accept responsibility for testing and validating functionality for their specific use case.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Testimonials Disclaimer</h2>
              <p>
                Testimonials and reviews displayed on our website represent individual experiences and opinions. Results may vary from person to person. Testimonials are not intended to represent or guarantee that anyone will achieve the same or similar results.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. Limitation of Liability</h2>
              <p>
                In no event shall SilverFox Media, its owner, employees, or affiliates be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:
              </p>
              <ul>
                <li>Your use of or inability to use our website or templates</li>
                <li>Any errors or omissions in content</li>
                <li>Unauthorized access to or use of our servers</li>
                <li>Any bugs, viruses, or malicious code transmitted through our services</li>
                <li>Any third-party content or conduct</li>
              </ul>
              <p>
                This limitation applies regardless of the legal theory under which damages are sought.
              </p>
            </div>

            <div className="legal-section">
              <h2>9. Indemnification</h2>
              <p>
                By using our templates and services, you agree to indemnify and hold harmless SilverFox Media from any claims, damages, or expenses arising from your use of the templates, including but not limited to claims related to the content you publish on websites built with our templates.
              </p>
            </div>

            <div className="legal-section">
              <h2>10. Changes to This Disclaimer</h2>
              <p>
                We reserve the right to modify this disclaimer at any time. Changes will be effective immediately upon posting to our website. Your continued use of our website or services after changes are posted constitutes acceptance of the modified disclaimer.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Disclaimer, please contact us:
              </p>
              <ul className="legal-contact">
                <li><strong>Email:</strong> james@silverfoxmedia.co</li>
                <li><strong>Phone:</strong> (972) 800-8105</li>
                <li><strong>Website:</strong> silverfoxmedia.co</li>
              </ul>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Disclaimer;
